import { PropsWithChildren, createContext, useCallback, useContext, useState } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  EmployeeForSelectFragment,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'
import { PerformanceReviewOverviewPageReviewProcessFragment } from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { FilterOptions } from 'modules/core/components/Filter/filters/AddFilter'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { SupportedFilters } from './types'

interface FiltersContext {
  filters: SupportedFilters
  setFilter: <K extends keyof SupportedFilters>(key: K, value: SupportedFilters[K]) => void
  addVisibleFilter: (filterKey: string) => void
  newlyAddedFilters: string[]
  availableFilters: FilterOptions
  setAvailableFilters: (filters: FilterOptions) => void
}

const initialFilters: SupportedFilters = {
  manager: null,
  employees: [],
  onlyDirectReports: true,
  agreementStatus: null,
  evaluationsStatus: null,
  reviewerStrategy: null,
  scores: [],
  teamIds: [],
  tenureDates: [],
  workplaceIds: [],
  legalEntityIds: [],
  additionalVisibleFilters: [],
}

const FiltersContext = createContext<FiltersContext>({
  filters: initialFilters,
  setFilter: () => {},
  addVisibleFilter: () => {},
  newlyAddedFilters: [],
  availableFilters: [],
  setAvailableFilters: () => {},
})

export const useFiltersValues = () => {
  const { filters } = useContext(FiltersContext)
  return filters
}

export const useFilter = <K extends keyof SupportedFilters>(filter: K) => {
  const { filters, setFilter } = useContext(FiltersContext)

  return {
    value: filters[filter],
    setValue: (value: SupportedFilters[K]) => setFilter(filter, value),
  }
}

export const useVisibleFilters = () => {
  const { filters, addVisibleFilter } = useContext(FiltersContext)
  return { visibleFilters: filters.additionalVisibleFilters, addVisibleFilter }
}

export const useNewlyAddedFilters = () => {
  const { newlyAddedFilters } = useContext(FiltersContext)
  return { newlyAddedFilters }
}

export const useAvailableFilters = () => {
  const { availableFilters, setAvailableFilters } = useContext(FiltersContext)
  return { availableFilters, setAvailableFilters }
}

export const isManagerFilterVisible = (
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
) => reviewProcess.targetManager.length > 1 || reviewProcess._policies?.fullVisibility.can

const defaultManager = ({
  initialValue,
  reviewProcess,
  currentEmployee,
}: {
  initialValue: EmployeeForSelectFragmentNoAccess | 'none' | null
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
  currentEmployee?: EmployeeForSelectFragment
}) => {
  const isTargetManager = (managerId?: number): managerId is number => {
    if (!managerId) return false
    return reviewProcess.targetManager.some((manager) => Number(manager.id) === managerId)
  }

  // "none" is used to identify when the user manually cleared the manager filter,
  // so we should keep the filter empty by not returning any employee.
  if (initialValue === 'none') return null

  if (isTargetManager(initialValue?.id) && isManagerFilterVisible(reviewProcess)) {
    return initialValue
  }

  return isTargetManager(currentEmployee?.id) ? currentEmployee : null
}

const defaultReviewerStrategy = ({
  initialValue,
  reviewProcess,
}: {
  initialValue: PerformanceReviewerStrategiesStrategyEnum | null
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
}) => {
  return initialValue ?? reviewProcess.reviewerStrategies?.[0] ?? null
}

interface FiltersProviderProps {
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
  currentEmployee?: EmployeeForSelectFragment
  availableFilters?: FilterOptions
}

export const FiltersProvider = ({
  reviewProcess,
  currentEmployee,
  children,
  availableFilters = [],
}: PropsWithChildren<FiltersProviderProps>) => {
  const [storedFilters, setStoredFilters] = useLocalStorage<Record<number, SupportedFilters>>(
    LOCAL_STORAGE_KEYS.performanceProcessShowFilters
  )

  const [reviewProcessFilters, setReviewProcessFilters] = useState<SupportedFilters>(() => {
    const storedReviewProcessFilters = storedFilters?.[reviewProcess.id]
    const filters = storedReviewProcessFilters ?? initialFilters

    return {
      ...filters,
      scores: filters.scores ?? [],
      teamIds: filters.teamIds ?? [],
      tenureDates: filters.tenureDates ?? [],
      workplaceIds: filters.workplaceIds ?? [],
      legalEntityIds: filters.legalEntityIds ?? [],
      additionalVisibleFilters: filters.additionalVisibleFilters ?? [],
      manager: defaultManager({
        initialValue:
          storedReviewProcessFilters && !storedReviewProcessFilters.manager
            ? 'none'
            : filters.manager,
        reviewProcess,
        currentEmployee,
      }),
      reviewerStrategy: defaultReviewerStrategy({
        initialValue: filters.reviewerStrategy,
        reviewProcess,
      }),
    }
  })

  const [newlyAddedFilters, setNewlyAddedFilters] = useState<string[]>([])
  const [availableFiltersState, setAvailableFiltersState] =
    useState<FilterOptions>(availableFilters)

  const setAvailableFilters = useCallback((filters: FilterOptions) => {
    setAvailableFiltersState(filters)
  }, [])

  const addVisibleFilter = useCallback(
    (filterKey: string) => {
      setReviewProcessFilters((prev) => {
        const newAdditionalVisibleFilters = prev.additionalVisibleFilters.includes(filterKey)
          ? prev.additionalVisibleFilters
          : [...prev.additionalVisibleFilters, filterKey]

        const newFilters = {
          ...prev,
          additionalVisibleFilters: newAdditionalVisibleFilters,
        }

        setStoredFilters({
          ...storedFilters,
          [reviewProcess.id]: newFilters,
        })

        return newFilters
      })

      // If the filter is not already visible, add it to the list of newly added filters
      if (!reviewProcessFilters?.additionalVisibleFilters?.includes(filterKey)) {
        setNewlyAddedFilters((prev) => [...prev, filterKey])

        // Timeout to display the filter as "open" when just added, providing visual feedback
        setTimeout(() => {
          setNewlyAddedFilters((prev) => prev.filter((filter) => filter !== filterKey))
        }, 100)
      }
    },
    [
      reviewProcess.id,
      reviewProcessFilters?.additionalVisibleFilters,
      storedFilters,
      setStoredFilters,
    ]
  )

  const setFilter = useCallback(
    <K extends keyof SupportedFilters>(key: K, value: SupportedFilters[K]) => {
      setReviewProcessFilters((prev) => {
        const newFilters = { ...prev, [key]: value }
        setStoredFilters({
          ...storedFilters,
          [reviewProcess.id]: newFilters,
        })
        return newFilters
      })
    },
    [reviewProcess.id, storedFilters, setStoredFilters]
  )

  return (
    <FiltersContext.Provider
      value={{
        filters: reviewProcessFilters,
        setFilter,
        addVisibleFilter,
        newlyAddedFilters,
        availableFilters: availableFiltersState,
        setAvailableFilters: setAvailableFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  )
}
